// Document card — used in the Documents tab (audience feed) and the Editor
// Corner (creator workspace).
//
// It shows: title, tag, description, first attachment preview, status,
// "New" tag (<15 days since publish), "Unread" tag (current user hasn't
// opened it), "Re-Edited (Nx)" tag with last-edit datetime, read count,
// countdown to schedule/approval, approval trail (My Posts view), and the
// action buttons.

import { Button } from "../ui/button";
import {
  CheckCircle2, Eye, Pencil, Trash2, Send, ShieldAlert, ExternalLink,
  Download, Star, RefreshCcw, Users as UsersIcon, Globe2, Clock, XCircle,
  FileText, UserPlus, MessageSquare,
} from "lucide-react";
import { fmtDateTimeIST } from "../../lib/datetime";
import {
  TAG_META, STATUS_META, fmtSize, fmtCountdown, attachmentFlavor, IMPORTANT_BADGE,
} from "./docConstants";

/** Small colored pill for a doc status. */
function StatusPill({ status }) {
  const s = STATUS_META[status] || STATUS_META.approved;
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 ${s.cls}`} data-testid={`doc-status-${status}`}>
      <Icon className="w-2.5 h-2.5" /> {s.label}
    </span>
  );
}

function TagPill({ tag, custom }) {
  const meta = TAG_META[tag] || TAG_META.other;
  const label = tag === "other" && custom ? custom : meta.label;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 ${meta.cls}`}>
      {label}
    </span>
  );
}

export default function DocumentCard({
  d, currentUserId, isMaster, onOpen, onEdit, onRemove, onPublish,
  onApprove, onReject, onReassign, onResubmit, onGoodToGo, context = "feed",
}) {
  const isCreator = (d.created_by_id || d.uploaded_by_id) === currentUserId;
  const canEdit = isMaster || (isCreator && (d.status === "draft" || d.status === "scheduled" || d.status === "rejected" || d.revise_pending));
  const canResubmit = isCreator && d.status === "rejected";
  const canDelete = isMaster || (isCreator && d.status === "draft");
  const showPublish = isCreator && (d.status === "draft" || d.status === "scheduled");
  const primaryAtt = (d.attachments && d.attachments[0]) || null;
  const { Icon: FIcon, cls: fCls } = attachmentFlavor(primaryAtt || {});

  const isPublicView = context === "feed";
  const showNew = isPublicView && d.is_new;
  const showUnread = isPublicView && d.status === "approved" && !d.i_read && !isCreator;
  const showRevised = (d.revision_count || 0) > 0;
  const IImp = IMPORTANT_BADGE.Icon;

  return (
    <div
      className="relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-indigo-300 dark:hover:border-indigo-600 hover:shadow-md transition-all cursor-pointer flex flex-col group"
      onClick={() => onOpen(d)}
      data-testid={`doc-card-${d.id}`}
    >
      {d.is_important && (
        <div className="absolute top-0 left-0 h-full w-1 bg-gradient-to-b from-rose-600 to-rose-700" />
      )}

      <div className="p-4 flex-1">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-lg ${fCls} flex items-center justify-center flex-shrink-0`}>
            <FIcon size={18} strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              <TagPill tag={d.tag} custom={d.tag_custom} />
              {d.is_important && (
                <span className={`inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 ${IMPORTANT_BADGE.cls}`} data-testid={`doc-important-${d.id}`}>
                  <IImp className="w-2.5 h-2.5" /> Important
                </span>
              )}
              {/* Show status pill for non-published states only — published
                  docs display "Published on <date>" in the card footer. */}
              {d.status !== "approved" && <StatusPill status={d.status} />}
              {showNew && <span className="text-[10px] uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 bg-gradient-to-r from-red-600 to-rose-600 text-white border-red-700" data-testid={`doc-tag-new-${d.id}`}><Star className="inline w-2.5 h-2.5 mr-0.5" />New</span>}
              {showUnread && <span className="text-[10px] uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700" data-testid={`doc-tag-unread-${d.id}`}>Unread</span>}
              {showRevised && (
                <span className="text-[10px] uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 bg-indigo-50 text-indigo-800 border-indigo-300 dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-700" title={d.last_revised_at ? `Last edited ${fmtDateTimeIST(d.last_revised_at)} by ${d.last_revised_by_name || ""}` : ""}>
                  <RefreshCcw className="inline w-2.5 h-2.5 mr-0.5" /> Re-Edited · {d.revision_count}x
                </span>
              )}
              {d.revise_pending && (
                <span
                  className="text-[10px] uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 bg-amber-500 text-white border-amber-600 animate-pulse"
                  data-testid={`doc-review-comments-tag-${d.id}`}
                  title="Reviewer submitted comments — awaiting editor to address them"
                >
                  <MessageSquare className="inline w-2.5 h-2.5 mr-0.5" /> Review Comments Submitted
                </span>
              )}
            </div>
            <h3 className="font-bold text-slate-900 dark:text-slate-100 text-[14.5px] leading-tight truncate" title={d.title}>{d.title}</h3>
            {d.description && <p className="text-[12px] text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">{d.description}</p>}

            {/* attachments summary */}
            {(d.attachments?.length || 0) > 0 && (
              <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
                <FileText className="w-3 h-3" />
                {d.attachments.length} attachment{d.attachments.length > 1 ? "s" : ""}
                {primaryAtt?.type === "file" && primaryAtt.size ? ` · ${fmtSize(primaryAtt.size)}` : ""}
              </div>
            )}

            {/* Countdowns */}
            {d.status === "scheduled" && d.seconds_left_to_schedule != null && (
              <div className="mt-1 text-[11px] text-amber-700 font-mono inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> Publishes in {fmtCountdown(d.seconds_left_to_schedule)}
              </div>
            )}
            {d.status === "pending_admin" && d.seconds_left_admin != null && !d.is_important && (
              <div className="mt-1 text-[11px] text-orange-700 font-mono inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> Admin auto-approve in {fmtCountdown(d.seconds_left_admin)}
              </div>
            )}
            {d.status === "pending_master" && d.seconds_left_master != null && !d.is_important && (
              <div className="mt-1 text-[11px] text-orange-700 font-mono inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> Master auto-approve in {fmtCountdown(d.seconds_left_master)}
              </div>
            )}
            {d.status === "pending_master" && d.is_important && (
              <div className="mt-1 text-[11px] text-rose-700 font-mono inline-flex items-center gap-1">
                <ShieldAlert className="w-3 h-3" /> Awaits Master Admin (no auto-approve)
              </div>
            )}
            {d.status === "rejected" && d.rejection_remarks && (
              <div className="mt-1 text-[11px] text-rose-700 truncate" title={d.rejection_remarks}>
                <XCircle className="inline w-3 h-3 mr-0.5" /> Rejected by {d.rejected_by_name}: {d.rejection_remarks}
              </div>
            )}

            {/* My-Posts context — show approval trail */}
            {(context === "mine" || context === "editor") && d.status !== "draft" && (
              <div className="mt-1 space-y-0.5 text-[11px] text-slate-600">
                {d.approved_by_admin_name && (
                  <div>Admin approved by <strong>{d.approved_by_admin_name}</strong> {d.approved_by_admin_at ? `· ${fmtDateTimeIST(d.approved_by_admin_at)}` : ""}</div>
                )}
                {d.status === "pending_master" && !d.approved_by_master_name && (
                  <div className="text-orange-700">Pending for Master Admin approval</div>
                )}
                {d.approved_by_master_name && (
                  <div>Master approved by <strong>{d.approved_by_master_name}</strong> {d.approved_by_master_at ? `· ${fmtDateTimeIST(d.approved_by_master_at)}` : ""}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* People strip — Editor + Approver */}
      <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-800 flex items-center gap-4 flex-wrap text-[11px]">
        <span className="text-slate-500 dark:text-slate-400">Editor: <span className="font-semibold text-slate-800 dark:text-slate-200">{d.original_creator_name || d.created_by_name || "—"}</span></span>
        <span className="text-slate-500 dark:text-slate-400">Approver: <span className="font-semibold text-slate-800 dark:text-slate-200">{
          d.approved_by_master_name || d.approved_by_admin_name || (d.status === "approved" ? "System auto-approved" : "—")
        }</span></span>
      </div>

      {/* Reviewer credit strip — visible on every card that had reviewers.
          Each reviewer shows name + when they signed off (Good to Go). */}
      {(d.reviewers || []).length > 0 && (
        <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-800 flex items-start gap-2 flex-wrap text-[11px]" data-testid={`doc-card-reviewers-${d.id}`}>
          <span className="text-slate-500 dark:text-slate-400 shrink-0 font-semibold">Reviewers:</span>
          <div className="flex flex-wrap gap-1.5">
            {d.reviewers.map((r, i) => (
              <span
                key={r.user_id || i}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${
                  r.good_to_go
                    ? "bg-emerald-50 text-emerald-800 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800"
                    : "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"
                }`}
                title={r.good_to_go && r.good_to_go_at ? `Approved on ${fmtDateTimeIST(r.good_to_go_at)}` : "Awaiting review"}
              >
                {r.good_to_go && <CheckCircle2 className="w-3 h-3" />}
                {r.name}
                {r.good_to_go && r.good_to_go_at && (
                  <span className="font-mono text-[10px] opacity-70">· {fmtDateTimeIST(r.good_to_go_at)}</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="px-4 py-2.5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-[10.5px] text-slate-600 dark:text-slate-300 min-w-0">
          {d.target_all ? (
            <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 font-bold"><Globe2 size={11} /> Everyone</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 font-bold" title={(d.target_user_names || []).join(", ")}>
              <UsersIcon size={11} /> {(d.target_user_names || []).length || 0}
            </span>
          )}
          {d.status === "approved" && d.published_at && (
            <>
              <span className="text-slate-400 dark:text-slate-600">·</span>
              <span className="font-mono text-slate-700 dark:text-slate-300" title="Published on">Published {fmtDateTimeIST(d.published_at)}</span>
            </>
          )}
          {d.status === "approved" && (
            <>
              <span className="text-slate-400 dark:text-slate-600">·</span>
              {d.tag === "company_policy" ? (
                <span className="inline-flex items-center gap-0.5 text-rose-700 dark:text-rose-300 font-semibold" title={`${d.policy_ack_count || 0} employee(s) accepted this policy`} data-testid={`doc-accept-count-${d.id}`}>
                  <ShieldAlert className="w-3 h-3" /> {d.policy_ack_count || 0} accepted
                </span>
              ) : d.can_view_reads !== false && (
                <span className="inline-flex items-center gap-0.5 text-indigo-700 dark:text-indigo-300 font-semibold" title={`${d.read_count} user(s) opened · ${d.total_opens || 0} total opens`} data-testid={`doc-view-count-${d.id}`}>
                  <Eye className="w-3 h-3" /> {d.read_count || 0} read
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {onApprove && (d.status === "pending_admin" || d.status === "pending_master") && (
            <Button size="sm" onClick={() => onApprove(d)} className="h-8 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`doc-approve-${d.id}`}>
              <CheckCircle2 size={10} className="mr-1" /> Approve
            </Button>
          )}
          {onGoodToGo && d.status === "pending_review" && (d.reviewers || []).some((r) => r.user_id === currentUserId && !r.good_to_go) && (
            <Button size="sm" onClick={() => onGoodToGo(d)} className="h-8 text-[10.5px] bg-violet-600 hover:bg-violet-700 text-white" data-testid={`doc-good-to-go-${d.id}`}>
              <CheckCircle2 size={10} className="mr-1" /> Good to Go
            </Button>
          )}
          {onReject && (d.status === "pending_admin" || d.status === "pending_master") && (
            <Button size="sm" variant="outline" onClick={() => onReject(d)} className="h-8 text-[10.5px] border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`doc-reject-${d.id}`}>
              <XCircle size={10} className="mr-1" /> Reject
            </Button>
          )}
          {canResubmit && (
            <Button size="sm" onClick={() => onResubmit(d)} className="h-8 text-[10.5px] bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`doc-resubmit-${d.id}`}>
              <RefreshCcw size={10} className="mr-1" /> Re-Edit &amp; Resubmit
            </Button>
          )}
          {showPublish && (
            <Button size="sm" onClick={() => onPublish(d)} className="h-8 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`doc-publish-${d.id}`}>
              <Send size={10} className="mr-1" /> Publish
            </Button>
          )}
          {onReassign && (
            <Button size="sm" onClick={() => onReassign(d)} className="h-8 text-[10.5px] bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`doc-reassign-${d.id}`}>
              <UserPlus size={10} className="mr-1" /> Reassign
            </Button>
          )}
          {canEdit && (
            <button onClick={() => onEdit(d)} className="p-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:bg-slate-800 hover:text-indigo-700" data-testid={`doc-edit-${d.id}`} title="Edit"><Pencil size={12} /></button>
          )}
          {canDelete && (
            <button onClick={() => onRemove(d)} className="p-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-rose-600 hover:text-white" data-testid={`doc-delete-${d.id}`} title="Delete"><Trash2 size={12} /></button>
          )}
        </div>
      </div>
    </div>
  );
}

// Re-export to avoid circular imports where needed.
export { StatusPill, TagPill };
